import { AlertTriangle, MessageCircle, Phone } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { WHATSAPP_URL } from "../lib/i18n";
import api from "../lib/api";

const EMERGENCY_MSG_EN = "URGENT MAINTENANCE — Equipment down. Please respond ASAP.";
const EMERGENCY_MSG_AR = "طارئ صيانة — معدات متوقفة. أرجو الرد بشكل عاجل.";

// Fire-and-forget log — never block the WhatsApp / call redirect
const logEmergency = (source, page = "") => {
  try {
    api.post("/emergency-clicks", {
      source,
      page: page || (typeof window !== "undefined" ? window.location.pathname : ""),
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 200) : "",
    }).catch(() => {});
  } catch { /* ignore */ }
};

export function EmergencyLine({ compact = false }) {
  const { t, lang } = useLang();
  const msg = lang === "ar" ? EMERGENCY_MSG_AR : EMERGENCY_MSG_EN;
  const url = `${WHATSAPP_URL}?text=${encodeURIComponent(msg)}`;

  if (compact) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        onClick={() => logEmergency("whatsapp_compact")}
        data-testid="emergency-line-compact"
        className="inline-flex items-center gap-2 bg-[#ef4444] hover:bg-[#dc2626] text-white text-xs font-semibold px-4 h-10 rounded-full shadow-md hover:shadow-lg transition-all"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
        </span>
        <AlertTriangle className="w-3.5 h-3.5" />
        {t.emergencyLine.compactLabel}
      </a>
    );
  }

  return (
    <section className="py-14 bg-gradient-to-r from-[#ef4444] via-[#dc2626] to-[#ef4444] text-white relative overflow-hidden" data-testid="emergency-line">
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: "repeating-linear-gradient(45deg, white 0px, white 2px, transparent 2px, transparent 40px)",
        }}
      />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 relative">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4 flex-1">
            <div className="relative shrink-0">
              <div className="w-14 h-14 rounded-2xl bg-white/15 border-2 border-white/30 backdrop-blur flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <span className="absolute -top-1 -end-1 w-4 h-4 rounded-full bg-white animate-pulse" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.35em] text-white/80 font-semibold uppercase mb-1">
                {t.emergencyLine.eyebrow}
              </div>
              <h2 className="font-display text-2xl sm:text-3xl leading-tight mb-1">
                {t.emergencyLine.title}
              </h2>
              <p className="text-white/85 text-sm max-w-xl">{t.emergencyLine.subtitle}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <a href={url} target="_blank" rel="noreferrer" onClick={() => logEmergency("whatsapp_full")} data-testid="emergency-whatsapp">
              <button className="inline-flex items-center gap-2 bg-white text-[#dc2626] hover:bg-[#0891b2] hover:text-white font-semibold px-5 h-12 rounded-lg shadow-lg transition-all">
                <MessageCircle className="w-4 h-4" />
                {t.emergencyLine.btnWhatsapp}
              </button>
            </a>
            <a href="tel:+97142882777" onClick={() => logEmergency("call")} data-testid="emergency-call">
              <button className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white border-2 border-white/30 font-semibold px-5 h-12 rounded-lg backdrop-blur transition-all">
                <Phone className="w-4 h-4" />
                {t.emergencyLine.btnCall}
              </button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
